<div class="receipt-panel-details">
  <form [formGroup]="mainForm">
    <!-- GROUP 1: Receipt Information and Line Items -->
    <div class="panel-content-unified group-receipt-info">
      <!-- Receipt Image and Basic Info -->
      <div class="unified-section receipt-header">
        <div
          class="receipt-image-container"
          style="position: relative"
          >
          <lib-photo
            [url]="receipt?.receiptUrl"
            [width]="'200px'"
            [height]="'280px'"
            [radius]="'8px'"
            class="receipt-image"
            >
          </lib-photo>

          <!-- Center buttons overlay -->
          <div class="image-action-buttons">
            <button
              mat-mini-fab
              class="image-action-btn"
              type="button"
              matTooltip="Open image in side panel"
              (click)="onImageClick()"
              >
              <mat-icon>zoom_in</mat-icon>
            </button>
            <button
              mat-mini-fab
              class="image-action-btn"
              type="button"
              matTooltip="Open image in new tab"
              (click)="openReceiptImage($event)"
              >
              <mat-icon>open_in_new</mat-icon>
            </button>
            <button
              mat-mini-fab
              class="image-action-btn"
              type="button"
              matTooltip="Open image with OCR text"
              (click)="openImageWithOcr()"
              >
              <mat-icon>document_scanner</mat-icon>
            </button>
          </div>
        </div>

        <div class="receipt-info">
          <div class="info-section">
            <h4 class="text-lg font-semibold text-neutral-900 mb-4">Receipt Information</h4>

            <div class="info-row">
              <span class="label text-sm font-medium text-neutral-700">Scan Date:</span>
              <span class="value text-sm text-neutral-900">{{ formatDate(receipt?.createDate) }}</span>
            </div>

            <div class="info-row">
              <span class="label text-sm font-medium text-neutral-700">User Phone:</span>
              <span class="value text-sm text-neutral-900">{{ receipt?.userPhoneNo || "N/A" }}</span>
            </div>

            <div class="info-row">
              <span class="label text-sm font-medium text-neutral-700">Verify Doc ID:</span>
              <span class="value text-sm text-neutral-900">{{
                receipt?.docId || receipt?.id || "N/A"
              }}</span>
            </div>

            <div class="info-row editable" [class.editing]="isEditingInvoiceNumber">
              <span class="label text-sm font-medium text-neutral-700">Invoice No:</span>
              <div class="input-with-actions">
                <input
                  type="text"
                  class="value-input text-sm"
                  formControlName="invoiceNumber"
                  maxlength="255"
                  placeholder="Enter invoice number"
                  (focus)="onInvoiceNumberFocus()"
                  (keydown.enter)="onInvoiceNumberEnter($event)"
                  />
                @if (isEditingInvoiceNumber) {
                  <div class="input-actions">
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn submit-btn"
                      (click)="submitInvoiceNumber()"
                      >
                      <mat-icon>check</mat-icon>
                    </button>
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn cancel-btn"
                      (click)="cancelInvoiceNumber()"
                      >
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>

            <div class="info-row editable" [class.editing]="isEditingInvoiceDate">
              <span class="label text-sm font-medium text-neutral-700">Invoice Date:</span>
              <div class="input-with-actions">
                <input
                  type="date"
                  class="value-input text-sm"
                  formControlName="invoiceDate"
                  (focus)="onInvoiceDateFocus()"
                  />
                @if (isEditingInvoiceDate) {
                  <div class="input-actions">
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn submit-btn"
                      (click)="submitInvoiceDate()"
                      >
                      <mat-icon>check</mat-icon>
                    </button>
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn cancel-btn"
                      (click)="cancelInvoiceDate()"
                      >
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>

            <div class="info-row editable" [class.editing]="isEditingInvoiceTime">
              <span class="label text-sm font-medium text-neutral-700">Invoice Time:</span>
              <div class="input-with-actions">
                <input
                  type="time"
                  class="value-input text-sm"
                  formControlName="invoiceTime"
                  (focus)="onInvoiceTimeFocus()"
                  />
                @if (isEditingInvoiceTime) {
                  <div class="input-actions">
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn submit-btn"
                      (click)="submitInvoiceTime()"
                      >
                      <mat-icon>check</mat-icon>
                    </button>
                    <button
                      mat-raised-button
                      type="button"
                      class="action-btn cancel-btn"
                      (click)="cancelInvoiceTime()"
                      >
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                }
              </div>
            </div>

            <div class="info-row">
              <span class="label text-sm font-medium text-neutral-700">Vendor:</span>
              <span class="value text-sm text-neutral-900">{{ receipt?.vendor?.name || "N/A" }}</span>
            </div>

            @if (shouldShowDuplicateOriginalLink()) {
              <div class="info-row">
                <span class="label text-sm font-medium text-neutral-700">Duplicate Original:</span>
                <a
                  href="javascript:void(0)"
                  class="duplicates-link text-blue-600 hover:text-blue-800"
                  (click)="openDuplicateOriginalReceipt()"
                  [class.disabled]="isLoadingOriginalReceipt"
                  [attr.aria-disabled]="isLoadingOriginalReceipt"
                  >
                  {{ isLoadingOriginalReceipt ? 'Loading...' : 'View Original Receipt' }}
                </a>
              </div>
            }

            @if (shouldShowDuplicateOriginalsLink()) {
              <div class="info-row">
                <span class="label text-sm font-medium text-neutral-700">Duplicate Originals:</span>
                <a
                  href="javascript:void(0)"
                  class="duplicates-link text-blue-600 hover:text-blue-800"
                  (click)="searchDuplicates()"
                  >
                  Search Duplicates
                </a>
              </div>
            }

            @if (hasDuplicates()) {
              <div class="info-row">
                <span class="label text-sm font-medium text-neutral-700">Duplicates:</span>
                <a
                  href="javascript:void(0)"
                  class="duplicates-link text-blue-600 hover:text-blue-800"
                  (click)="searchDuplicates()"
                  >
                  {{ getDuplicatesText() }}
                </a>
              </div>
            }
          </div>
          <!-- End info-section -->
        </div>
        <!-- End receipt-info -->
      </div>
      <!-- End unified-section receipt-header -->

      <!-- Line Items Section -->
      <div class="unified-section line-items-section">
        <div class="section-header">
          <h4 class="text-lg font-semibold text-neutral-900">Line Items ({{ lineItemsArray.length }})</h4>
          <button
            mat-icon-button
            (click)="addLineItem()"
            *appHasAnyPermission="[
              permissions.CAMPAIGNMANAGEMENT_WRITE,
              permissions.CAMPAIGN_UPDATE
            ]"
            >
            <mat-icon>add</mat-icon>
          </button>
        </div>

        <div formArrayName="lineItems" class="line-items-list">
          @for (item of lineItemsArray.controls; track $index) {
            <div class="line-item-card" [formGroupName]="$index">
              <!-- Display Mode -->
              @if (!item.get('_editing')?.value) {
                <div class="line-item-display">
                  <div class="item-header">
                    <div class="item-title-section">
                      <strong class="text-sm font-medium text-neutral-900">{{ item.get("description")?.value }}</strong>
                      <div class="item-category text-xs text-neutral-600 italic">
                        {{ item.get("category")?.value }}
                      </div>
                    </div>
                    <span class="item-total text-sm font-semibold text-neutral-900"
                      >${{ (item.get("total")?.value || 0).toFixed(2) }}</span
                      >
                    </div>
                    <div class="item-details-row">
                      <div class="details-left text-xs text-neutral-600">
                        <span>Qty: {{ item.get("quantity")?.value || 0 }}</span>
                        <span
                          >Price: ${{
                          (item.get("price")?.value || 0).toFixed(2)
                          }}</span
                          >
                          <span
                            >Discount: ${{
                            (item.get("discount")?.value || 0).toFixed(2)
                            }}</span
                            >
                          </div>
                          <div class="item-actions">
                            <button
                              mat-icon-button
                              (click)="editLineItem($index)"
                              class="hover:bg-blue-50 rounded-full"
                              title="Edit"
                              >
                              <mat-icon class="text-blue-600">edit</mat-icon>
                            </button>
                            <button
                              mat-icon-button
                              (click)="deleteLineItem($index)"
                              class="hover:bg-red-50 rounded-full"
                              title="Delete"
                              >
                              <mat-icon class="text-red-600">close</mat-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    } @else {
                      <!-- Edit Mode -->
                      <div class="line-item-edit">
                        <div class="edit-header-row">
                          <mat-form-field class="description-field" appearance="fill">
                            <input
                              matInput
                              formControlName="description"
                              maxlength="255"
                              placeholder="Description *"
                              />
                            @if (item.get('description')?.hasError('required')) {
                              <mat-error>Description is required</mat-error>
                              } @if (item.get('description')?.hasError('maxlength')) {
                              <mat-error>Maximum 255 characters</mat-error>
                            }
                          </mat-form-field>
                          <div class="edit-total-field">
                            <input
                              formControlName="total"
                              type="number"
                              step="0.01"
                              class="total-input"
                              />
                          </div>
                        </div>

                        <mat-form-field class="category-field" appearance="fill">
                          <input
                            matInput
                            formControlName="category"
                            placeholder="Category"
                            />
                        </mat-form-field>

                        <div class="edit-numbers-row">
                          <div class="edit-number-field">
                            <span class="number-label">Qty:</span>
                            <input
                              formControlName="quantity"
                              matInput
                              type="number"
                              class="number-input"
                              />
                          </div>

                          <div class="edit-number-field">
                            <span class="number-label">Price:</span>
                            <input
                              formControlName="price"
                              matInput
                              type="number"
                              step="0.01"
                              class="number-input"
                              />
                          </div>

                          <div class="edit-number-field">
                            <span class="number-label">Discount:</span>
                            <input
                              formControlName="discount"
                              matInput
                              type="number"
                              step="0.01"
                              class="number-input"
                              />
                          </div>
                        </div>
                      </div>
                    }
                  </div>

                  <!-- Edit Actions -->
                  @if (item.get('_editing')?.value) {
                    <div class="edit-actions">
                      <button
                        mat-raised-button
                        type="button"
                        class="action-btn submit-btn"
                        (click)="saveLineItem($index)"
                        title="Save"
                        >
                        <mat-icon>check</mat-icon>
                      </button>
                      <button
                        mat-raised-button
                        type="button"
                        class="action-btn cancel-btn"
                        (click)="cancelEditLineItem($index)"
                        title="Cancel"
                        >
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  }
                }
              </div>
            </div>
          </div>

          <!-- GROUP 2: Campaign Information, Tags, Admin Notes, JSON, and History -->
          <div class="panel-content-unified group-campaign-data">
            <!-- Campaign Information Section -->
            <div class="unified-section campaign-info-section">
              <h4 class="text-lg font-semibold text-neutral-900 mb-4">Campaign Information</h4>

              @if (campaignInfo && receiptDetails.length > 0) {
                <mat-tab-group
                  [(selectedIndex)]="selectedTabIndex"
                  (selectedIndexChange)="onTabChange($event)"
                  animationDuration="0ms"
                  [disableRipple]="false"
                  class="campaign-tabs-group"
                  [class.single-tab-layout]="receiptDetails.length === 1"
                  [class.two-tab-layout]="receiptDetails.length === 2"
                  [class.scrollable-tab-layout]="receiptDetails.length > 2"
                  [class.centered-tabs]="receiptDetails.length === 2"
                  [class.slide-left]="tabAnimationDirection === 'left'"
                  [class.slide-right]="tabAnimationDirection === 'right'"
                  >
                  @for (detail of receiptDetails; track detail.campaign?.campaignId; let
                    i = $index) {
                    <mat-tab>
                      <ng-template mat-tab-label>
                        <span
                          class="tab-label-text"
                          [title]="detail.campaign?.title || 'Campaign ' + (i + 1)"
                          >
                          {{ detail.campaign?.title || 'Campaign ' + (i + 1) }}
                        </span>
                      </ng-template>
                      <div class="campaign-details-content">
                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">Current Decision:</span>
                          <span
                            class="decision-badge px-2 py-1 text-xs font-medium rounded-full"
                            [style.background-color]="getDecisionColor()"
                            >
                            {{ getDecision() }}
                          </span>
                        </div>

                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">Campaign ID:</span>
                          <span class="campaign-value campaign-id-value text-sm text-neutral-900">{{ campaignInfo.campaignId || "N/A" }}</span>
                          <button mat-icon-button (click)="openCampaignDetail()" title="Open Campaign Detail" class="campaign-link-btn">
                            <mat-icon>open_in_new</mat-icon>
                          </button>
                        </div>

                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">Receipt ID:</span>
                          <span class="campaign-info-spacer"></span>
                          <span class="campaign-value text-sm text-neutral-900 campaign-value-right">{{ receipt?.id || 'N/A' }}</span>
                          <button mat-icon-button (click)="openReceiptDetail()" title="Open Receipt Detail" class="campaign-link-btn">
                            <mat-icon>open_in_new</mat-icon>
                          </button>
                        </div>

                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">Campaign Type:</span>
                          <span class="campaign-value text-sm text-neutral-900">{{ getCampaignType() }}</span>
                        </div>

                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">Start Date:</span>
                          <span class="campaign-value text-sm text-neutral-900">{{
                            formatDate(campaignInfo.startDate)
                          }}</span>
                        </div>

                        <div class="campaign-info-row">
                          <span class="campaign-label text-sm font-medium text-neutral-700">End Date:</span>
                          <span class="campaign-value text-sm text-neutral-900">{{
                            formatDate(campaignInfo.endDate)
                          }}</span>
                        </div>

                        <!-- Show only Points field for Points-based campaigns -->
                        @if (isPointsBasedCampaign()) {
                          <div class="campaign-info-row editable-row" [class.editing]="isEditingPoints">
                            <span class="campaign-label text-sm font-medium text-neutral-700">Points Received:</span>
                            <div class="input-with-actions-campaign">
                              <input
                                type="number"
                                class="text-sm px-3 py-2 input-stamps-points"
                                [class.input-error]="isPointsInputInvalid"
                                [(ngModel)]="pointsReceived"
                                [ngModelOptions]="{ standalone: true }"
                                (focus)="onPointsFocus()"
                                (input)="onPointsInput($event)"
                                min="1"
                                [placeholder]="getPointsPlaceholder()"
                                [title]="pointsAlreadyEarned > 0 ? 'User already earned ' + pointsAlreadyEarned + ' points' : ''"
                                />
                              @if (isPointsInputInvalid && isEditingPoints) {
                                <div class="input-error-message">
                                  Points must be greater than {{ pointsAlreadyEarned }}
                                </div>
                              }
                              @if (isEditingPoints) {
                                <div class="input-actions">
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn submit-btn"
                                    (click)="submitPoints()"
                                    [disabled]="isPointsInputInvalid"
                                    >
                                    <mat-icon>check</mat-icon>
                                  </button>
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn cancel-btn"
                                    (click)="cancelPoints()"
                                    >
                                    <mat-icon>close</mat-icon>
                                  </button>
                                </div>
                              }
                            </div>
                          </div>
                        } @else if (isStampsBasedCampaign()) {
                          <!-- Show only Stamps field for Stamps-based campaigns (Punch Card) -->
                          <div class="campaign-info-row editable-row" [class.editing]="isEditingStamps">
                            <span class="campaign-label text-sm font-medium text-neutral-700">Stamps Received:</span>
                            <div class="input-with-actions-campaign">
                              <input
                                type="number"
                                class="text-sm px-3 py-2 input-stamps-points"
                                [class.input-error]="isStampsInputInvalid"
                                [(ngModel)]="stampsReceived"
                                [ngModelOptions]="{ standalone: true }"
                                (focus)="onStampsFocus()"
                                (input)="onStampsInput($event)"
                                min="1"
                                [placeholder]="getStampsPlaceholder()"
                                [title]="stampsAlreadyEarned > 0 ? 'User already earned ' + stampsAlreadyEarned + ' stamps' : ''"
                                />
                              @if (isStampsInputInvalid) {
                                <div class="input-error-message">
                                  Stamps must be greater than {{ stampsAlreadyEarned }}
                                </div>
                              }
                              @if (isEditingStamps) {
                                <div class="input-actions">
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn submit-btn"
                                    (click)="submitStamps()"
                                    [disabled]="isStampsInputInvalid"
                                    >
                                    <mat-icon>check</mat-icon>
                                  </button>
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn cancel-btn"
                                    (click)="cancelStamps()"
                                    >
                                    <mat-icon>close</mat-icon>
                                  </button>
                                </div>
                              }
                            </div>
                          </div>
                        } @else {
                          <!-- Show both fields for other campaign types -->
                          <div class="campaign-info-row editable-row" [class.editing]="isEditingPoints">
                            <span class="campaign-label text-sm font-medium text-neutral-700">Points Received:</span>
                            <div class="input-with-actions-campaign">
                              <input
                                type="number"
                                class="text-sm px-3 py-2 input-stamps-points"
                                [class.input-error]="isPointsInputInvalid"
                                [(ngModel)]="pointsReceived"
                                [ngModelOptions]="{ standalone: true }"
                                (focus)="onPointsFocus()"
                                (input)="onPointsInput($event)"
                                min="1"
                                [placeholder]="getPointsPlaceholder()"
                                [title]="pointsAlreadyEarned > 0 ? 'User already earned ' + pointsAlreadyEarned + ' points' : ''"
                                />
                              @if (isPointsInputInvalid) {
                                <div class="input-error-message">
                                  Points must be greater than {{ pointsAlreadyEarned }}
                                </div>
                              }
                              @if (isEditingPoints) {
                                <div class="input-actions">
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn submit-btn"
                                    (click)="submitPoints()"
                                    [disabled]="isPointsInputInvalid"
                                    >
                                    <mat-icon>check</mat-icon>
                                  </button>
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn cancel-btn"
                                    (click)="cancelPoints()"
                                    >
                                    <mat-icon>close</mat-icon>
                                  </button>
                                </div>
                              }
                            </div>
                          </div>

                          <div class="campaign-info-row editable-row" [class.editing]="isEditingStamps">
                            <span class="campaign-label text-sm font-medium text-neutral-700">Stamps Received:</span>
                            <div class="input-with-actions-campaign">
                              <input
                                type="number"
                                class="text-sm px-3 py-2 input-stamps-points"
                                [class.input-error]="isStampsInputInvalid"
                                [(ngModel)]="stampsReceived"
                                [ngModelOptions]="{ standalone: true }"
                                (focus)="onStampsFocus()"
                                (input)="onStampsInput($event)"
                                min="1"
                                [placeholder]="getStampsPlaceholder()"
                                [title]="stampsAlreadyEarned > 0 ? 'User already earned ' + stampsAlreadyEarned + ' stamps' : ''"
                                />
                              @if (isStampsInputInvalid) {
                                <div class="input-error-message">
                                  Stamps must be greater than {{ stampsAlreadyEarned }}
                                </div>
                              }
                              @if (isEditingStamps) {
                                <div class="input-actions">
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn submit-btn"
                                    (click)="submitStamps()"
                                    [disabled]="isStampsInputInvalid"
                                    >
                                    <mat-icon>check</mat-icon>
                                  </button>
                                  <button
                                    mat-raised-button
                                    type="button"
                                    class="action-btn cancel-btn"
                                    (click)="cancelStamps()"
                                    >
                                    <mat-icon>close</mat-icon>
                                  </button>
                                </div>
                              }
                            </div>
                          </div>
                        }

                        <!-- Tags Section (inside tab) -->
                        <div class="tab-section tags-section">
                          <div class="tags-title-row">
                            <h4 class="text-base font-semibold text-neutral-900">Tags</h4>
                            <button
                              mat-icon-button
                              (click)="toggleTagEditMode()"
                              class="tag-toggle-btn"
                              [attr.aria-label]="isTagEditMode ? 'Close tag editor' : 'Add tag'"
                              >
                              <mat-icon>{{ isTagEditMode ? 'close' : 'add' }}</mat-icon>
                            </button>
                          </div>

                          <div class="tags-chips-container">
                            @if (tags && detail.receipt.tags.length > 0) { @for (tag of detail.receipt.tags; track $index)
                              {
                              <mat-chip
                                [removable]="isTagEditMode"
                                (removed)="removeTag(tag)"
                                class="bg-neutral-100 text-neutral-800 text-sm"
                                >
                                {{ tag }}
                                @if (isTagEditMode) {
                                  <mat-icon matChipRemove class="text-neutral-600">close</mat-icon>
                                }
                              </mat-chip>
                              } }
                            </div>

                            @if (isTagEditMode) {
                              <div class="tag-add-row">
                                <input
                                  class="tag-input-field"
                                  [(ngModel)]="newTag"
                                  [ngModelOptions]="{ standalone: true }"
                                  [matAutocomplete]="autoTag"
                                  (input)="filterTagSuggestions()"
                                  (keydown.enter)="addTag()"
                                  placeholder="Type to add or search tags..."
                                  />
                                <mat-autocomplete #autoTag="matAutocomplete" (optionSelected)="onTagSelected($event)">
                                  @for (suggestion of filteredTagSuggestions; track suggestion) {
                                    <mat-option [value]="suggestion">{{ suggestion }}</mat-option>
                                  }
                                </mat-autocomplete>
                                <button class="add-tag-btn" type="button" (click)="addTag()">
                                  <mat-icon>add</mat-icon>
                                  <span>Add Tag</span>
                                </button>
                              </div>
                            }
                          </div>
                          <!-- End tab-section tags-section -->

                          <!-- Admin Notes Section (inside tab) -->
                          <div class="tab-section notes-edit-section" [class.editing]="isEditingNote">
                            <h4 class="text-base font-semibold text-neutral-900 mb-2">Admin Notes</h4>
                            <div class="custom-textarea-wrapper">
                              <textarea
                                [value]="detail.receipt.note || ''"
                                (input)="onNoteInput($event)"
                                (focus)="onNoteFocus()"
                                maxlength="5000"
                                placeholder="Enter admin notes..."
                                class="custom-textarea"
                              ></textarea>
                            </div>

                            @if (isEditingNote) {
                              <div class="note-actions">
                                <button
                                  mat-raised-button
                                  type="button"
                                  class="action-btn submit-btn"
                                  (click)="submitNote()"
                                  >
                                  <mat-icon>check</mat-icon>
                                </button>
                                <button
                                  mat-raised-button
                                  type="button"
                                  class="action-btn cancel-btn"
                                  (click)="cancelNote()"
                                  >
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                          <!-- End tab-section notes-edit-section -->

                          <!-- Receipt JSON Data Section (inside tab) -->
                          <div class="tab-section json-section">
                            <h4 class="text-base font-semibold text-neutral-900 mb-2">Receipt JSON Data</h4>

                            <div class="json-subsection">
                              <div class="json-display-container">
                                <button
                                  mat-icon-button
                                  class="copy-json-button"
                                  (click)="copyJsonToClipboard()"
                                  title="Copy JSON"
                                  >
                                  <mat-icon>content_copy</mat-icon>
                                </button>
                                <div class="json-display">
                                  <pre class="text-xs text-neutral-900">{{ raw | json }}</pre>
                                </div>
                              </div>
                            </div>
                          </div>
                          <!-- End tab-section json-section -->

                          <!-- Changelog History Section (inside tab) -->
                          <div class="tab-section changelog-section">
                            <div class="changelog-title-row">
                              <h4 class="text-base font-semibold text-neutral-900">
                                Changelog History
                              </h4>
                              @if (receiptChanges && receiptChanges.length > 0) {
                                <button
                                  mat-icon-button
                                  (click)="openChangelogDialog()"
                                  class="changelog-expand-btn"
                                  matTooltip="View full changelog history"
                                  >
                                  <mat-icon>open_in_full</mat-icon>
                                </button>
                              }
                            </div>
                            @if (receiptChanges && receiptChanges.length > 0) {
                              <lib-ngx-data-table
                                [columns]="columns"
                                [data]="receiptChanges"
                                [selectionType]="undefined"
                                rowHeight="auto"
                                class="changelog-table"
                                >
                              </lib-ngx-data-table>
                            } @else {
                              <div class="no-changelog text-sm text-neutral-600">No history records available</div>
                            }
                          </div>
                          <!-- End tab-section changelog-section -->
                        </div>
                        <!-- End campaign-details-content -->
                      </mat-tab>
                    }
                  </mat-tab-group>
                }
              </div>
            </div>
          </form>

          <div class="fixed-bottom-actions">
            @if (getLastEditInfo()) {
              <div class="last-edit-info">
                {{ getLastEditInfo() }}
              </div>
            }

            <button
      *appHasAnyPermission="[
        permissions.CAMPAIGNMANAGEMENT_WRITE,
        permissions.CAMPAIGN_UPDATE
      ]"
              [disabled]="checkEligibleDisabled"
              class="action-button eligible-button full-width text-sm font-medium"
              [class.eligible-active]="isCheckEligibleChecked || (showActionUndoButton && currentActionType === 'eligible')"
              (click)="checkEligible()"
              >
              @if (isCheckEligibleChecked || (showActionUndoButton && currentActionType === 'eligible')) {
                <mat-icon
                  class="action-state-icon"
                  >
                  check
                </mat-icon>
              }
              <span class="action-button-label">
                @if (showActionUndoButton && currentActionType === 'eligible') {
                  Sent for Eligibility check. UNDO ({{ actionUndoCountdown }}s)
                } @else {
                  Check Eligibility
                }
              </span>
            </button>

            <div class="bottom-buttons-row">
              <button
        *appHasAnyPermission="[
          permissions.CAMPAIGNMANAGEMENT_WRITE,
          permissions.CAMPAIGN_UPDATE
        ]"
                [disabled]="checkFraudDisabled"
                class="action-button fraud-button text-sm font-medium"
                [class.fraud-active]="isCheckFraudChecked || (showActionUndoButton && currentActionType === 'fraud')"
                (click)="checkFraud()"
                >
                @if (isCheckFraudChecked || (showActionUndoButton && currentActionType === 'fraud')) {
                  <mat-icon
                    class="action-state-icon"
                    >
                    check
                  </mat-icon>
                }
                <span class="action-button-label">
                  @if (showActionUndoButton && currentActionType === 'fraud') {
                    Sent for Fraud check. UNDO ({{ actionUndoCountdown }}s)
                  } @else {
                    Check Fraud
                  }
                </span>
              </button>

              <button
        *appHasAnyPermission="[
          permissions.CAMPAIGNMANAGEMENT_WRITE,
          permissions.CAMPAIGN_UPDATE
        ]"
                [disabled]="rejectDisabled"
                class="action-button reject-button text-sm font-medium"
                [class.reject-active]="isRejectChecked || (showActionUndoButton && currentActionType === 'reject')"
                (click)="reject()"
                >
                @if (isRejectChecked || (showActionUndoButton && currentActionType === 'reject')) {
                  <mat-icon
                    class="action-state-icon"
                    >
                    check
                  </mat-icon>
                }
                <span class="action-button-label">
                  @if (showActionUndoButton && currentActionType === 'reject') {
                    Mark as Rejected. UNDO ({{ actionUndoCountdown }}s)
                  } @else {
                    Mark as Rejected
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
